import { Icon, Markdown } from '@capsico/ui';
import { LEGAL_DRAFT_BANNER } from '@capsico/contracts';
import { Button } from './ui';

/*
 * W21 legal (presentation only): the blocking "We have updated our terms" sheet and the
 * document body. State and data live in hooks/useLegal.ts.
 */

export interface ReconsentDoc {
  documentId: string;
  title: string;
  version: number;
  summaryOfChanges: string | null;
  body: string;
}

export interface ReconsentLabels {
  title: string;
  body: string;
  whatChanged: string;
  version: (version: number) => string;
  read: string;
  hide: string;
  agree: string;
  accept: string;
  signOut: string;
}

/** Full-height blocking sheet: no close button and no scrim tap — only Accept (or sign out). */
export function ReconsentSheet({
  docs,
  open,
  agreed,
  accepting,
  labels,
  resolveHref,
  onToggle,
  onAgree,
  onAccept,
  onSignOut,
}: {
  docs: ReconsentDoc[];
  open: string | null;
  agreed: boolean;
  accepting: boolean;
  labels: ReconsentLabels;
  resolveHref: (href: string) => string | null;
  onToggle: (documentId: string) => void;
  onAgree: (next: boolean) => void;
  onAccept: () => void;
  onSignOut?: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reconsent-title"
    >
      <div className="absolute inset-0 bg-scrim" />
      <div className="relative mx-auto flex max-h-sheet-max w-full max-w-120 flex-col rounded-t-sheet bg-surface pb-[env(safe-area-inset-bottom)] animate-[rise_200ms_ease-out]">
        <div className="px-5 pt-5">
          <span className="inline-flex size-12 items-center justify-center rounded-full bg-brand-soft text-brand-ink">
            <Icon name="shield" size={24} />
          </span>
          <h2 id="reconsent-title" className="mt-3 text-lg font-bold text-ink">
            {labels.title}
          </h2>
          <p className="mt-1 text-sm text-ink-2">{labels.body}</p>
        </div>
        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {docs.map((doc) => (
            <section
              key={doc.documentId}
              className="rounded-sheet shadow-[inset_0_0_0_1px_var(--color-hair)]"
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-title font-semibold text-ink">{doc.title}</p>
                  <span className="shrink-0 rounded-full bg-tile px-2 py-0.5 text-caption text-ink-2 tabular">
                    {labels.version(doc.version)}
                  </span>
                </div>
                {doc.summaryOfChanges && (
                  <div className="mt-2 rounded-field bg-brand-soft px-3 py-2">
                    <p className="text-micro font-semibold tracking-wide text-ink-2 uppercase">
                      {labels.whatChanged}
                    </p>
                    <p className="mt-0.5 text-xs text-ink">{doc.summaryOfChanges}</p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => onToggle(doc.documentId)}
                  aria-expanded={open === doc.documentId}
                  className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-ink"
                >
                  {open === doc.documentId ? labels.hide : labels.read}
                  <Icon
                    name="chevron-down"
                    size={16}
                    className={open === doc.documentId ? 'rotate-180' : ''}
                  />
                </button>
              </div>
              {open === doc.documentId && (
                <div className="max-h-80 overflow-y-auto px-4 pb-4 shadow-[inset_0_1px_0_var(--color-hair)]">
                  <Markdown
                    className="pt-3"
                    size="sm"
                    source={doc.body}
                    bannerPrefix={LEGAL_DRAFT_BANNER}
                    resolveHref={resolveHref}
                  />
                </div>
              )}
            </section>
          ))}
        </div>
        <div className="space-y-3 px-5 py-4 shadow-[inset_0_1px_0_var(--color-hair)]">
          <label className="flex cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(event) => onAgree(event.target.checked)}
              className="mt-0.5 size-5 shrink-0 accent-brand"
            />
            <span className="text-sm text-ink">{labels.agree}</span>
          </label>
          <Button full disabled={!agreed} busy={accepting} onClick={onAccept}>
            {labels.accept}
          </Button>
          {onSignOut && (
            <button
              type="button"
              onClick={onSignOut}
              className="block w-full text-center text-xs font-semibold text-ink-2"
            >
              {labels.signOut}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** A published legal document (Account › Legal). */
export function LegalDocBody({
  title,
  meta,
  body,
  resolveHref,
}: {
  title: string;
  meta: string;
  body: string;
  resolveHref: (href: string) => string | null;
}) {
  return (
    <article>
      <h1 className="text-headline font-bold text-ink">{title}</h1>
      <p className="mt-1 text-caption text-ink-3">{meta}</p>
      <Markdown
        className="mt-4"
        size="sm"
        source={body}
        bannerPrefix={LEGAL_DRAFT_BANNER}
        resolveHref={resolveHref}
      />
    </article>
  );
}
